import { useContext, useState } from 'react';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';

import GhostButton from '../../shared/button/GhostButton';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { BodyText } from '../../shared/typography/PrimaryTypography';
import {
  retireMetricAlias,
  toProblemDetail
} from '../../../services/syntiro_honey/data_ingestion.service';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetricAliasItem, ProblemDetail } from '../../../types/syntiro_honey';

interface RetireRuleDialogProps {
  /** The ACTIVE rule being retired. */
  rule: MetricAliasItem;
  /** Close without retiring. */
  onClose: () => void;
  /** Called after a successful retire so the parent refreshes its list. */
  onRetired: () => void;
}

/**
 * Reviewer undo of one ACTIVE learned rule: the label stops mapping
 * automatically for this client, and the rule is kept as history with the
 * reviewer's reason (audit, never delete). The reason is required — a retired
 * rule with no explanation is unreadable a month later.
 * @returns {JSX.Element}
 */
export default function RetireRuleDialog({ rule, onClose, onRetired }: RetireRuleDialogProps) {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [reason, setReason] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);
  const [problem, setProblem] = useState<ProblemDetail | null>(null);

  const handleRetire = async (): Promise<void> => {
    setBusy(true);
    setProblem(null);
    try {
      await retireMetricAlias(rule.id, rule.companyId, reason.trim());
      setFeedbackData({
        state: true,
        type: 'success',
        message: `Rule retired — "${rule.rawLabel}" will no longer map automatically for this client.`
      });
      onRetired();
    } catch (error: unknown) {
      setProblem(toProblemDetail(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Retire this learned rule?</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <BodyText>
          &ldquo;{rule.rawLabel}&rdquo; will stop mapping automatically to {rule.metricName} for
          this client. The rule is kept as history — future uploads of this label go back to AI
          suggestion and review.
        </BodyText>
        {problem !== null && (
          <Alert severity="error">{problem.detail ?? problem.title ?? 'Retiring failed.'}</Alert>
        )}
        <TextField
          label="Reason"
          size="small"
          fullWidth
          multiline
          minRows={2}
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          helperText="Kept on the rule so the retirement stays explainable later."
        />
      </DialogContent>
      <DialogActions>
        <GhostButton dark onClick={onClose} disabled={busy}>
          Cancel
        </GhostButton>
        <PrimaryButton
          onClick={() => void handleRetire()}
          disabled={reason.trim() === '' || busy}
          loading={busy}
        >
          Retire rule
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}
